import { useCallback, useEffect, useRef, useState } from "react";
import { get } from "utils/DeApi";
import { getTextResponses } from "../utils/extractMLKeywords";

const useFetchAuditMLSentimentAnalysis = ({ auditId }) => {
  const subscribedPromises = useRef([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [textResponses, setTextResponses] = useState([]);

  const fetchAudit = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const responsesPromise = get(`audits/${auditId}/responses`);
    subscribedPromises.current.push(responsesPromise);

    try {
      const { data: responses = [] } = await responsesPromise.promise;
      const rawTextResponses = getTextResponses(responses);
      const parsedTextResponses = Array.isArray(rawTextResponses)
        ? rawTextResponses
        : [];
      setTextResponses(parsedTextResponses);

      let fetchedSummary = [];

      if (parsedTextResponses.length > 0) {
        const summaryPromise = get(`ml/audits/${auditId}/summary`);
        subscribedPromises.current.push(summaryPromise);

        const summaryResponse = await summaryPromise.promise;
        fetchedSummary = summaryResponse.data ?? [];
        setSummary(fetchedSummary);
      } else {
        setSummary([]);
      }

      return {
        summary: fetchedSummary,
        textResponses: parsedTextResponses,
      };
    } catch (error) {
      if (!error.isCanceled) setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    if (auditId) {
      fetchAudit();
    }
  }, [auditId, fetchAudit]);

  useEffect(() => {
    return () => {
      subscribedPromises.current.forEach((p) => p.cancel?.());
    };
  }, []);

  return {
    error,
    summary,
    setError,
    isLoading,
    textResponses,
  };
};

export default useFetchAuditMLSentimentAnalysis;
